<template>
	<div class="speech" :style="position">
		<div ref="measure" class="speech__bubble speech__bubble--measure"></div>
		<transition name="drop-in">
			<div v-if="visible" class="speech__bubble speech__bubble--visible">
				<span class="speech__bubble__valign">
					<span v-for="c in chars" :key="c.key" :class="c.class">{{
						c.char
					}}</span>
				</span>
			</div>
		</transition>
	</div>
</template>

<script>
import wait from '@/mixins/wait'
import xyPos from '@/mixins/xyPos'

const LINE_HEIGHT = 22
const MAX_LINES = 2
const CHAR_MS = 40

// How long text should linger before disappearing
// if there is more text to follow
const LINGER_MS = 1000
const FADE_IN_MS = 300
const FADE_OUT_MS = 500

export default {
	name: 'Speech',
	data() {
		return {
			queue: [],
			visible: false,
			chars: [],
			speaking: false,
			progress: 0,
			disappear: false,
			tween: null
		}
	},
	methods: {
		getChars(text) {
			return [...text].map((char, i) => {
				const visible = i < this.progress
				return {
					char,
					key: `${i}_${char}`,
					class: ['char', { 'char--visible': visible }]
				}
			})
		},
		getLines(text) {
			const { measure } = this.$refs
			if (!measure) return Infinity

			// Set innerText and measure offsetHeight
			measure.innerText = text
			const lines = Math.ceil(measure.offsetHeight / LINE_HEIGHT)
			measure.innerText = ''
			return lines
		},
		fits(text) {
			return this.getLines(text) <= MAX_LINES
		},
		/**
		 * Warning: this is SLOW! Don't pass a large string (e.g. the entire
		 * Bee Movie script) into this function, the program will hang.
		 */
		split(text) {
			// Split text down into smaller chunks if it won't all fit
			const length = text.length
			const chunks = []
			const space = ' '
			let start = 0

			while (start < length) {
				// Put cursor at the end of the string
				let cursor = length
				let snippet

				while (cursor > start) {
					// Check if current segment fits
					snippet = text.slice(start, cursor)
					if (this.fits(snippet)) break

					// Move cursor backwards a word at a time
					cursor = text.lastIndexOf(space, cursor - 1)

					// If we catch up with start, there's probably a very long word here
					if (cursor <= start) {
						const next = text.indexOf(space, start + 1)
						cursor = next > -1 ? next : length
						break
					}
				}

				// Add snippet to chunks and move start up to cursor position
				chunks.push(snippet)
				start = text[cursor] === space ? cursor + 1 : cursor
			}

			return chunks
		},
		async add(text = '', override = false) {
			let chunks
			if (Array.isArray(text)) {
				// Split each array entry into chunks, then flatten
				chunks = text.reduce((acc, str) => {
					return acc.concat(this.split(str))
				}, [])
			} else {
				chunks = this.split(text)
			}
			if (override) this.queue = chunks
			else this.queue.push(...chunks)
		},
		async start() {
			// Start new speech if anything in queue
			if (this.queue.length) {
				const next = this.queue.shift()

				// Stop current speech
				if (this.visible) await this.stop(false)

				// Determine how long to tween for
				const to = next.length
				const duration = to * CHAR_MS

				// Wait for fade in then start talking
				this.visible = true
				await this.wait(FADE_IN_MS)
				this.$emit('speaking', true)

				this.tween = this.addTween(
					{ from: 0, to, duration },
					step => {
						this.progress = Math.round(step)
						this.chars = this.getChars(next)
					},
					async done => {
						if (done) {
							// Linger for a bit then start again
							await this.wait(LINGER_MS)
							this.tween = null
							this.start()
						}
					}
				)
			} else {
				// Finish speaking, disappear if set
				this.$emit('speaking', false)
				if (this.disappear) await this.stop()
			}
		},
		stop(stopAnim = true) {
			// Stop all current timeouts and tweens
			this.cancelWait()
			if (this.tween) this.removeTween(this.tween)

			// Stop animation if called explicitly
			if (stopAnim) this.$emit('speaking', false)

			// Remove current text and wait for fade out
			this.visible = false
			this.chars = []
			this.progress = 0
			return this.wait(FADE_OUT_MS)
		},
		setDisappear(disappear = true) {
			this.disappear = disappear
		}
	},
	beforeDestroy() {
		this.removeTween(this.tween)
		window.clearTimeout(this.timeout)
	},
	mixins: [wait, xyPos]
}
</script>

<style lang="scss" scoped>
@import '~scss/vars';
@import '~scss/layout';
@import '~scss/components';
@import '~scss/transitions';

@include drop-in(250ms);

.char {
	color: $white;
	&--visible {
		color: $blue-48;
	}
}

.speech {
	@include speech-bubble;
	position: absolute;
	user-select: none;
}
</style>
